import React, { useEffect, useMemo, useRef, useState } from "react";

// region Imports - External libraries
import _ from "lodash";
import Loading from "react-fullscreen-loading";
// endregion Imports - External libraries
// region Imports - Shared
import { IParamQueryValues } from "@shared/interfaces/filter.interface";
import { FilterApplicationTypesID } from "@shared/constants/filter-application-types.enum";
import { LocationFilterOptions, RegionalFilterOption, LocationTypeFilterOption } from "@shared/interfaces/location-filter-options.interface";
import { Location } from "@shared/interfaces/location.interface";
import { PaginationProperties, StandardReturnObject } from "@shared/utils/schemas/standard-return-object";
import { ParamNameTypes } from "@shared/constants/param-name-types.enum";
// endregion Imports - Shared
// region Imports - Store
import { states } from "@store/ducks/Locations/LocationsForm/locations-form.type";
// endregion Imports - Store
// region Imports - Languages
import useTranslation from "src/translations/useTranslation";
import { ToastMessages, LocationsModuleMessages, LocationTypeMessages, GlobalMessages } from "@shared/languages/interfaces";
// endregion Imports - Languages
// region Imports - Services
import api from "@services/api";
// endregion Imports - Services
// region Imports - Hooks
import { useToast } from "@hooks/useToast";
// endregion Imports - Hooks
// region Imports - Organisms
import GenericQueryFilter, {
  IMultipleSelectionOptions,
  IOnFilterReturn,
  ISimpleInputOptions
} from "@organisms/GenericQueryFilter";
// endregion Imports - Organisms

// region Interfaces
interface IProps {
  open: boolean;
  onClose: () => void;
  onFilter: (
    numberOfFilteredOptions: number,
    returnedData: Location[],
    pagination?: PaginationProperties
  ) => void;
  loadMoreToggle: boolean;
}
// endregion Interfaces

const QueryFilterLocations: React.FC<IProps> = ({ open, onClose, onFilter, loadMoreToggle }) => {

  // region Hooks
  const { t } = useTranslation();
  const { addToast } = useToast();
  // endregion Hooks
  // region States
  const [optionsRegionalFilter, setOptionsRegionalFilter] = useState<RegionalFilterOption[]>([]);
  const [optionsLocationTypeFilter, setOptionsLocationTypeFilter] = useState<LocationTypeFilterOption[]>([]);

  const [loadingRegionalFilter, setLoadingRegionalFilter] = useState(true);
  const [loadingLocationTypeFilter, setLoadingLocationTypeFilter] = useState(true);
  const [loadingUfFilter, setLoadingUfFilter] = useState(true);

  const [loadingLocations, setLoadingLocations] = useState(false);
  // endregion useState
  // region Constants
  const paramNameRegionals = ParamNameTypes.ID_REGIONALS;
  const paramNameLocationTypes = ParamNameTypes.ID_LOCATION_TYPES;
  const paramNameUf = ParamNameTypes.UF;
  const paramNameFinishPoint = ParamNameTypes.FINISH_POINT;
  const paramNameName = "name";
  const recordLimitPerFetch = 200;

  const regionalFilterOptions: IMultipleSelectionOptions = useMemo(() => ({
    isLoadingValues: loadingRegionalFilter,
    labelName: t(LocationsModuleMessages.regional),
    paramName: paramNameRegionals,
    values: optionsRegionalFilter.map(
      (regional) => ({
        value: regional.id_regional!,
        label: regional.name!
      })
    )
  }), [loadingRegionalFilter, optionsRegionalFilter, paramNameRegionals, t]);

  const locationTypeFilterOptions: IMultipleSelectionOptions = useMemo(() => ({
    isLoadingValues: loadingLocationTypeFilter,
    labelName: t(LocationsModuleMessages.type),
    paramName: paramNameLocationTypes,
    values: optionsLocationTypeFilter.map(
      (locationType) => ({
        value: locationType.id_location_type!,
        label: t(LocationTypeMessages[locationType.id_location_type!])
      })
    )
  }), [loadingLocationTypeFilter, optionsLocationTypeFilter, paramNameLocationTypes, t]);

  const ufFilterOptions: IMultipleSelectionOptions = useMemo(() => ({
    isLoadingValues: loadingUfFilter,
    labelName: "UF",
    paramName: paramNameUf,
    values: states.map((state) => ({
      value: state.abbreviation,
      label: state.description
    }))
  }), [loadingUfFilter, paramNameUf]);

  const finishPointFilterOptions: IMultipleSelectionOptions = useMemo(() => ({
    isLoadingValues: false,
    labelName: t(LocationsModuleMessages.detailsAllowTravelClosure),
    paramName: paramNameFinishPoint,
    values: [
      { value: "true", label: t(GlobalMessages.yes) },
      { value: "false", label: t(GlobalMessages.no) }
    ]
  }), [paramNameFinishPoint, t]);

  const nameFilterOptions: ISimpleInputOptions = useMemo(() => ({
    labelName: t(LocationsModuleMessages.locationNameFilter),
    paramName: paramNameName,
    values: []
  }), [paramNameName, t]);

  // endregion Constants
  // region Refs
  const paramsFilterRef = useRef<IParamQueryValues[]>([]);
  const offsetRef = useRef<number | undefined>(undefined);
  // endregion Refs
  // region Functions

  /**
   * Load filter options from the API.
   */
  const loadFilterOptions = async () => {

    try {

      const { data }: { data: StandardReturnObject<LocationFilterOptions> } = await api.get("locations/filter-options");

      if (data.status === "success") {
        setOptionsRegionalFilter(data.result?.regionals ?? []);
        setOptionsLocationTypeFilter(data.result?.locationTypes ?? []);
      } else {
        addToast({ type: "info", title: t(ToastMessages.alert), description: data.message });
      }

    } catch (error) {
      if (!error.response) addToast({ type: "error", title: t(ToastMessages.error), description: t(ToastMessages.connectionNotEstablished) });
      else addToast({ type: "error", title: error.response.data.backend, description: error.response.data.message });
    } finally {
      setLoadingRegionalFilter(false);
      setLoadingLocationTypeFilter(false);
      setLoadingUfFilter(false);
    }
  };

  const handleFilter = async (paramsData: IParamQueryValues[], loadMore: boolean) => {

    setLoadingLocations(true);

    paramsFilterRef.current = paramsData;

    if (loadMore) offsetRef.current! += recordLimitPerFetch;
    else offsetRef.current = 0;

    try {

      const mappedParams = paramsData.reduce((acc, param) => {
        acc[param.paramName] = param.paramValue;

        return acc;
      }, {} as { [key: string]: string | string[] });

      // The name filter input produces an array of values, but the backend expects a single free text value
      const nameParamValue = mappedParams[paramNameName];

      const { data } = await api.post("locations/get-filtered-by", {
        ...mappedParams,
        [paramNameName]: Array.isArray(nameParamValue) ? nameParamValue[0] : nameParamValue,
        offset: offsetRef.current,
        limit: recordLimitPerFetch
      });

      const validParamsData = paramsData.filter((item) => !_.isEmpty(item.paramValue));

      if (data.status !== "success") addToast({ type: "info", title: t(ToastMessages.alert), description: data.message });

      onFilter(
        validParamsData.length,
        data.result ?? [] as Location[],
        { totalCount: data.pagination?.totalCount ?? 0, limit: recordLimitPerFetch, offset: offsetRef.current ?? 0 }
      );

      return { status: "success" } as IOnFilterReturn;
    } catch (error) {
      if (!error.response) addToast({ type: "error", title: t(ToastMessages.error), description: t(ToastMessages.connectionNotEstablished) });
      else addToast({ type: "error", title: error.response.data.backend, description: error.response.data.message });

      return { status: "error" } as IOnFilterReturn;
    } finally {
      setLoadingLocations(false);
    }
  };
  // endregion Functions
  // region Effects

  // Load options from the API
  useEffect(() => {

    loadFilterOptions().then();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load more records with the last applied filter when the load more button is clicked
  useEffect(() => {
    if (offsetRef.current === undefined) {

      return;
    }

    handleFilter(paramsFilterRef.current, true).then();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadMoreToggle]);
  // endregion Effects

  return (
    <>
      <Loading loading={loadingLocations} />
      <GenericQueryFilter
        open={open}
        onClose={onClose}
        applicationTypeID={FilterApplicationTypesID.LOCATIONS}
        onFilter={(paramsData) => handleFilter(paramsData, false)}
        multipleSelectionOptions={[
          regionalFilterOptions,
          locationTypeFilterOptions,
          ufFilterOptions,
          finishPointFilterOptions
        ]}
        simpleInputOptions={[nameFilterOptions]}
      />
    </>
  );
};

export default QueryFilterLocations;
